import React, { useEffect, useState } from 'react';
import { MoonshotCreateTeamForm } from './MoonshotCreateTeamForm';
import { MoonshotPlanSelection } from './MoonshotPlanSelection';
import { MoonshotPayment } from './MoonshotPayment';
import { MoonshotBackground } from './MoonshotBackground';
import { MoonshotSuccess } from './MoonshotSuccess';
import { Campaigns, MoonshotSteps } from 'app/common/data/campaigns';
import {
  useMoonshotCampaignQuery,
  useMoonshotUpdateCampaignMutation,
} from '@trello/graphql/generated';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { MoonshotProgressIndicator } from './MoonshotProgressIndicator';
import styles from './Moonshot.less';
import { MoonshotInviteTeamMembers } from './MoonshotInviteTeamMembers';
import { MoonshotStep, MoonshotSubmitFunction } from './types';
import { idToDate } from '@trello/dates';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { Spinner } from 'app/src/components/Spinner';

const trelloLogo = require('resources/images/trello-logo-blue.svg');

const defaultStep: MoonshotStep = {
  step: 1,
  key: MoonshotSteps.CREATE_TEAM,
  noun: Noun.TEAM_CREATION,
  render: (onSubmit) => <MoonshotCreateTeamForm onSubmit={onSubmit} />,
};

const moonshotSteps: Map<MoonshotSteps, MoonshotStep> = new Map([
  [defaultStep.key, defaultStep],
  [
    MoonshotSteps.INVITE,
    {
      step: 2,
      key: MoonshotSteps.INVITE,
      noun: Noun.TEAM_INVITATION,
      render: (onSubmit: MoonshotSubmitFunction, orgId: string) =>
        orgId ? (
          <MoonshotInviteTeamMembers onSubmit={onSubmit} orgId={orgId} />
        ) : null,
      // check forced because of TS and other steps
      // null is an unlikely scenario, in order to get to
      // this step, one must create a team
    },
  ],
  [
    MoonshotSteps.PAYMENT,
    {
      step: 3,
      key: MoonshotSteps.PAYMENT,
      noun: Noun.BILLING,
      render: (onSubmit: MoonshotSubmitFunction) => (
        <MoonshotPlanSelection onSubmit={onSubmit} />
      ),
    },
  ],
  [
    MoonshotSteps.PAYMENT_FORM,
    {
      // keeping the step as 3 to keep the progress indicator
      // showing the PAYMENT step and PAYMENT_FORM step as one
      step: 3,
      key: MoonshotSteps.PAYMENT_FORM,
      noun: Noun.BILLING,
      render: (onSubmit: MoonshotSubmitFunction) => (
        <MoonshotPayment onSubmit={onSubmit} />
      ),
    },
  ],
  [
    MoonshotSteps.SUCCESS,
    {
      step: 4,
      key: MoonshotSteps.SUCCESS,
      noun: Noun.SUCCESS,
      render: (onSubmit: MoonshotSubmitFunction) => (
        <MoonshotSuccess onSubmit={onSubmit} />
      ),
    },
  ],
  [
    MoonshotSteps.WELCOME,
    {
      step: 5,
      key: MoonshotSteps.WELCOME,
      noun: Noun.SUCCESS, // this wont be used
      render: (onSubmit: MoonshotSubmitFunction) => null, // this wont be used
    },
  ],
]);

const useMoonshot = () => {
  const [updateCampaign] = useMoonshotUpdateCampaignMutation();
  const { data, loading, refetch } = useMoonshotCampaignQuery();

  const member = data && data.member;
  let orgId: string | undefined;
  if (member && member.idOrganizations.length) {
    const id = member.idOrganizations.sort((a, b) => {
      return idToDate(b).getTime() - idToDate(a).getTime();
    })[0];
    orgId = id;
  }

  const campaign =
    member && member.campaigns.find((c) => c.name === Campaigns.MOONSHOT);
  let currentStep = defaultStep;
  if (campaign) {
    const step = moonshotSteps.get(campaign.currentStep as MoonshotSteps)!.key;
    currentStep = moonshotSteps.get(step)!;
  } else if (!campaign && !loading) {
    // Looks like the graphql query is being ran before classic creates a campaign so the graphql data is stale
    refetch();
  }

  const getNextStep = () =>
    [...moonshotSteps.values()].find((s) => s.step === currentStep.step + 1);

  const goToStep = async (nextStep: MoonshotStep | undefined) => {
    if (!nextStep || !member || !campaign) {
      return;
    }

    try {
      await updateCampaign({
        variables: {
          id: campaign.id,
          currentStep: nextStep.key,
        },
      });
      refetch();
    } catch (err) {
      const errMsg =
        'There was a problem continuing to the next step. Please refresh and try again.';
      Alerts.showLiteralText(errMsg, 'error', 'error', 5000);
      throw new Error(errMsg);
    }

    if (nextStep.key === MoonshotSteps.WELCOME) {
      const team = member.organizations[0];

      if (!team) {
        return null;
      }

      window.location.assign(team.url);
    }
  };

  const onSubmit: MoonshotSubmitFunction = async (key) => {
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SUBMITS,
      directObj: currentStep.noun,
      method: 'by clicking next button',
      context: {
        step: currentStep.key,
      },
    });

    let nextStep = getNextStep();
    if (key) {
      nextStep = [...moonshotSteps.values()].find((s) => s.key === key);
    }

    goToStep(nextStep);
  };

  const [delayedLoading, setDelayedLoading] = useState(false);

  useEffect(() => {
    if (loading) {
      setTimeout(() => setDelayedLoading(true), 3000);
    } else {
      setDelayedLoading(false);
    }
  }, [loading]);

  return {
    currentStep,
    onSubmit,
    orgId,
    loading,
    delayedLoading,
  };
};

export const Moonshot: React.FunctionComponent = () => {
  const {
    currentStep,
    onSubmit,
    orgId,
    loading,
    delayedLoading,
  } = useMoonshot();

  return (
    <div className={styles.moonshot}>
      <header className={styles.header}>
        <img alt="Trello Logo" src={trelloLogo} className={styles.logo} />
        <MoonshotProgressIndicator
          activeStep={loading ? 0 : currentStep.step}
        />
      </header>
      <main className={styles.content}>
        {loading ? (
          delayedLoading ? (
            <Spinner centered />
          ) : null
        ) : (
          currentStep.render(onSubmit, orgId)
        )}
      </main>
      <MoonshotBackground />
    </div>
  );
};
